import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { Plus, PencilSimple, Trash, X, UserCircle, Check, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const blank = {
  name: "", email: "", branch_ids: [], phone: "",
  password: "agent123", active: true, google_sso_enabled: false,
};

export default function Agents() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [agents, setAgents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [counts, setCounts] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [showInactive, setShowInactive] = useState(true);
  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  const load = async () => {
    const [a, b, an] = await Promise.all([
      api.get("/agents"), api.get("/branches"), api.get("/analytics/overview"),
    ]);
    setAgents(a.data); setBranches(b.data); setCounts(an.data.by_agent || {});
  };
  useEffect(() => { load(); }, []);

  const visibleAgents = useMemo(
    () => agents.filter((a) => showInactive || a.active),
    [agents, showInactive],
  );

  const openNew = () => {
    setForm({ ...blank, branch_ids: branches[0] ? [branches[0].id] : [] });
    setEditing("new");
  };
  const openEdit = (ag) => {
    setForm({
      name: ag.name, email: ag.email,
      branch_ids: ag.branch_ids?.length ? ag.branch_ids : (ag.branch_id ? [ag.branch_id] : []),
      phone: ag.phone || "", password: "", active: ag.active !== false,
      google_sso_enabled: ag.google_sso_enabled === true,
    });
    setEditing(ag);
  };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/agents", form);
        toast.success("Agent added");
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/agents/${editing.id}`, payload);
        toast.success("Agent updated");
      }
      close(); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleActive = async (ag) => {
    const next = !ag.active;
    try {
      await api.patch(`/agents/${ag.id}`, { active: next });
      toast.success(next ? `${ag.name} enabled` : `${ag.name} disabled`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const kickSessions = async (ag) => {
    if (!window.confirm(`Force-logout ${ag.name}? All their active sessions will end immediately — they'll need to log in again with OTP.`)) return;
    try {
      const { data } = await api.post(`/sessions/user/${ag.id}/kick-all`);
      if (data.sessions_revoked > 0) toast.success(`Kicked ${data.sessions_revoked} session(s) for ${ag.name}`);
      else toast.message(`${ag.name} has no active sessions`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quickMigrateEmail = async (ag) => {
    // Build firstname.lastname@misterhair.in from the agent's name
    const slug = (ag.name || "").toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s]/g, "")
      .trim()
      .split(/\s+/)
      .join(".");
    if (!slug) { toast.error("Agent has no usable name to build the email"); return; }
    const suggested = `${slug}@misterhair.in`;
    const finalEmail = window.prompt(
      `Change ${ag.name}'s email\n\nCurrent: ${ag.email}\nNew (edit if needed):`,
      suggested,
    );
    if (!finalEmail || finalEmail === ag.email) return;
    try {
      await api.patch(`/agents/${ag.id}`, { email: finalEmail.trim().toLowerCase() });
      toast.success(`Email updated · ${ag.name} now logs in with ${finalEmail}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkMigrateEmails = async () => {
    const nonCompany = agents.filter((a) => a.email && !a.email.endsWith("@misterhair.in"));
    if (nonCompany.length === 0) {
      toast.success("All agents already use @misterhair.in emails");
      return;
    }
    const plan = nonCompany.map((a) => {
      const slug = (a.name || "").toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s]/g, "")
        .trim()
        .split(/\s+/)
        .join(".");
      return { agent: a, newEmail: slug ? `${slug}@misterhair.in` : null };
    }).filter((p) => p.newEmail);
    if (plan.length === 0) { toast.error("No agents have usable names to build emails from"); return; }
    const preview = plan.map((p) => `${p.agent.name}: ${p.agent.email} → ${p.newEmail}`).join("\n");
    if (!window.confirm(`Migrate ${plan.length} agent email(s)?\n\n${preview}\n\nExisting leads, names, and passwords are preserved. Pending OTPs for old emails will be invalidated.`)) return;
    let ok = 0, fail = 0;
    for (const p of plan) {
      try {
        await api.patch(`/agents/${p.agent.id}`, { email: p.newEmail });
        ok += 1;
      } catch (e) {
        console.error("migrate failed", p.agent.email, e);
        fail += 1;
      }
    }
    if (fail === 0) toast.success(`Migrated ${ok} agent email(s)`);
    else toast.error(`Migrated ${ok}, ${fail} failed (name collision or dupe email)`);
    load();
  };

  const remove = async (ag) => {
    if (!window.confirm(`Permanently delete agent "${ag.name}"? Their existing leads will keep referencing them — consider Disable instead.`)) return;
    try {
      await api.delete(`/agents/${ag.id}`);
      toast.success("Agent deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleBranchInForm = (bid) => {
    setForm((f) => {
      const has = f.branch_ids.includes(bid);
      return { ...f, branch_ids: has ? f.branch_ids.filter((x) => x !== bid) : [...f.branch_ids, bid] };
    });
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Team"
        title="Telesales agents"
        description="Manage the telesales team. Disable agents who aren't using the CRM (they'll be excluded from round-robin assignment but their existing leads stay)."
        actions={isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={bulkMigrateEmails} data-testid="bulk-migrate-emails"
              title="Change every non-company agent to firstname.lastname@misterhair.in"
              className="px-3 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10 rounded-sm">
              Migrate all → @misterhair.in
            </button>
            <button onClick={openNew} data-testid="add-agent-button"
              className="px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2">
              <Plus size={16} weight="bold" /> Add agent
            </button>
          </div>
        )}
      />

      <div className="px-8 pt-6">
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="agents-show-inactive" className="accent-primary" />
          Show inactive
          <span className="ml-2 text-foreground/70 normal-case tracking-normal">
            ({agents.filter((a) => !a.active).length} disabled)
          </span>
        </label>
      </div>

      <div className="px-8 py-4">
        <div className="border border-border bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold">Agent</th>
                <th className="px-4 py-3 font-bold">Email</th>
                <th className="px-4 py-3 font-bold">Branches</th>
                <th className="px-4 py-3 font-bold">WhatsApp</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold text-right">Leads</th>
                {isAdmin && <th className="px-4 py-3 font-bold text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="row-stagger">
              {visibleAgents.map((a) => {
                const bids = a.branch_ids?.length ? a.branch_ids : (a.branch_id ? [a.branch_id] : []);
                return (
                  <tr key={a.id}
                    className={`border-b border-border last:border-0 hover:bg-secondary/40 group ${!a.active ? "opacity-50" : ""}`}
                    data-testid={`agent-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-secondary flex items-center justify-center">
                          <UserCircle size={18} />
                        </div>
                        <span className="font-medium">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={a.email && !a.email.endsWith("@misterhair.in") ? "text-[#F5A623]" : ""}>
                          {a.email}
                        </span>
                        {isAdmin && a.email && !a.email.endsWith("@misterhair.in") && (
                          <button
                            onClick={() => quickMigrateEmail(a)}
                            data-testid={`agent-migrate-email-${a.id}`}
                            title="Change to firstname.lastname@misterhair.in"
                            className="text-[9px] uppercase tracking-[0.15em] font-bold px-1.5 py-0.5 border border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/10"
                          >→ Company email</button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {bids.length === 0 ? (
                        <span className="text-foreground/30">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {bids.map((bid) => (
                            <span key={bid} className="inline-block px-2 py-0.5 text-[10px] font-medium bg-secondary border border-border rounded-sm">
                              {branchMap[bid] || bid.slice(0, 6)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {a.phone ? `+${a.phone}` : <span className="text-foreground/30">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button
                          onClick={() => isAdmin && toggleActive(a)}
                          disabled={!isAdmin}
                          data-testid={`agent-status-${a.id}`}
                          title={isAdmin ? `Click to ${a.active ? "disable" : "enable"} ${a.name}` : ""}
                          className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold border ${
                            a.active
                              ? "border-[#008A00] text-[#008A00] bg-[#008A00]/10 hover:bg-[#008A00]/20"
                              : "border-muted-foreground text-muted-foreground bg-muted-foreground/5 hover:bg-muted-foreground/15"
                          } ${isAdmin ? "cursor-pointer" : "cursor-default"}`}>
                          {a.active ? "Active" : "Disabled"}
                        </button>
                        {a.google_sso_enabled && (
                          <span
                            data-testid={`agent-google-sso-chip-${a.id}`}
                            title="Google sign-in is enabled for this agent"
                            className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold border border-[#4285F4] text-[#4285F4] bg-[#4285F4]/10"
                          >
                            Google SSO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-display font-bold tabular-nums">{counts[a.id] || 0}</td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => kickSessions(a)} data-testid={`kick-sessions-${a.id}`}
                            title="Force-logout all active sessions for this user"
                            className="p-1.5 text-[#F5A623] hover:bg-[#F5A623]/10 border border-transparent hover:border-[#F5A623]/40">
                            <SignOut size={14} />
                          </button>
                          <button onClick={() => openEdit(a)} data-testid={`edit-agent-${a.id}`}
                            className="p-1.5 hover:bg-secondary border border-transparent hover:border-border">
                            <PencilSimple size={14} />
                          </button>
                          <button onClick={() => remove(a)} data-testid={`delete-agent-${a.id}`}
                            className="p-1.5 text-destructive hover:bg-destructive/10 border border-transparent hover:border-destructive/40">
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {visibleAgents.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">No agents to show.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
          <div className="bg-white border border-border w-full max-w-md fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="font-display text-2xl font-bold tracking-tight">
                {editing === "new" ? "New agent" : "Edit agent"}
              </h2>
              <button onClick={close} className="p-1.5 hover:bg-secondary"><X size={16} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <Field label="Name *">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="agent-form-name"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
              </Field>
              <Field label="Email *">
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="agent-form-email"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      const slug = (form.name || "").toLowerCase()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/[^a-z0-9\s]/g, "")
                        .trim()
                        .split(/\s+/)
                        .join(".");
                      if (slug) setForm((f) => ({ ...f, email: `${slug}@misterhair.in` }));
                    }}
                    disabled={!form.name}
                    data-testid="agent-suggest-company-email"
                    className="text-[10px] uppercase tracking-[0.15em] font-bold text-primary hover:underline disabled:opacity-40 disabled:no-underline"
                  >Use @misterhair.in</button>
                  {editing !== "new" && editing?.email && form.email !== editing.email && (
                    <span className="text-[11px] text-[#F5A623]">
                      Changing from <code className="font-mono">{editing.email}</code> → OTPs will now go to the new address.
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Branches">
                <div className="border border-border rounded-sm bg-white max-h-44 overflow-y-auto"
                  data-testid="agent-form-branches">
                  {branches.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No branches yet — add one first.</div>
                  ) : branches.map((b) => {
                    const checked = form.branch_ids.includes(b.id);
                    return (
                      <label key={b.id}
                        className={`flex items-center gap-2 px-3 py-2 text-sm border-b border-border last:border-0 cursor-pointer ${checked ? "bg-secondary" : "hover:bg-secondary/40"}`}
                        data-testid={`agent-form-branch-${b.id}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleBranchInForm(b.id)}
                          className="accent-primary" />
                        <span className="flex-1">{b.name}</span>
                        {checked && <Check size={14} weight="bold" className="text-primary" />}
                      </label>
                    );
                  })}
                </div>
                <span className="text-[10px] text-muted-foreground mt-1 block">
                  An agent can be assigned to multiple branches. Leads visible to a Branch Manager
                  cover ALL of their branches.
                </span>
              </Field>
              <Field label="WhatsApp number (with country code, digits only)">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="agent-form-phone"
                  placeholder="919876543210"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm font-mono focus:border-primary focus:outline-none" />
                <span className="text-[10px] text-muted-foreground mt-1 block">
                  Used for the WhatsApp deep-link on assigned leads. Leave empty to disable.
                </span>
              </Field>
              <Field label={editing === "new" ? "Password" : "New password (optional)"}>
                <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="agent-form-password"
                  placeholder={editing === "new" ? "agent123" : "Leave blank to keep current"}
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm font-mono focus:border-primary focus:outline-none" />
              </Field>
              <Field label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="agent-form-active" className="accent-primary" />
                  <span>Active</span>
                  <span className="text-xs text-muted-foreground">
                    — Disabled agents are excluded from round-robin lead assignment.
                  </span>
                </label>
              </Field>
              <Field label="">
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                    checked={form.google_sso_enabled}
                    onChange={(e) => setForm({ ...form, google_sso_enabled: e.target.checked })}
                    data-testid="agent-google-sso-toggle"
                    className="accent-primary" />
                  <span>Google sign-in enabled</span>
                  <span className="text-xs text-muted-foreground">
                    — Allow this agent to sign in with the Gmail address above (personal or company).
                  </span>
                </label>
              </Field>

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={close} className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">Cancel</button>
                <button type="submit" data-testid="agent-form-submit" className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                  {editing === "new" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      {label && <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-1.5">{label}</span>}
      {children}
    </label>
  );
}
